/*
 * $Id:: ChildAccountCard.js 2021/03/01 10:34 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';

import { globals as globalsSlice } from '../../../app/globalsSlice';
import inputsMaxChars from '../../../app/inputsMaxChars';
import { ReactComponent as CheckedIcon } from '../../../assets/images/check_box_24px.svg';
import { ReactComponent as UncheckedIcon } from '../../../assets/images/check_box_outline_blank_24px.svg';
import { isEmail } from '../../../common/utils/service';
import Card from '../../../components/Card';
import ErrorInput from '../../../components/ErrorInput';
import { t } from '../../../providers';

const useStyles = makeStyles((theme) => ({
    fieldLabel: {
        color: theme.palette.text.label,
        fontSize: '0.625rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 'normal',
    },
    fieldValue: {
        color: theme.palette.text.value,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
    },
    textInput: {
        height: '2rem',
        width: '9rem',
        borderRadius: '2px',
        marginTop: '10px',
        marginBottom: '10px',
        '@media (max-width:600px)': {
            width: '9.5rem',
        },
    },
    inputError: {
        marginBottom: '10px',
        fontSize: '0.75rem',
    },
    rolesTitle: {
        fontSize: '0.875rem',
        fontWeight: '600',
        color: theme.palette.text.outlined,
        marginTop: '20px',
        marginLeft: '-5px',
    },
    rolesDescription: {
        fontSize: '0.875rem',
        color: 'black',
        marginTop: '10px',
    },
    rolesForm: {
        marginTop: '10px',
    },
    rolesLabel: {
        marginBottom: '22px',
        fontSize: '0.875rem',
        color: 'black',
    },
    roleOption: {
        '& .MuiFormControlLabel-label': {
            color: 'black',
            fontSize: '0.875rem',
            fontWeight: '400',
        },
    },
}));

const ChildAccountCard = ({ saveAccount, cancelAction, childAccount = {}, readonly, error, isEdit }) => {
    const classes = useStyles();
    const { globals } = useSelector(globalsSlice);
    const dispatch = useDispatch();

    const { authorities } = globals;

    const [account, setAccount] = useState({
        firstName: {
            value: childAccount.firstName || '',
            label: 'first.name',
            placeholder: 'type.first.name',
            isDirty: !!childAccount.value,
            maxChars: inputsMaxChars.firstName,
        },
        lastName: {
            value: childAccount.lastName || '',
            label: 'last.name',
            placeholder: 'type.last.name',
            isDirty: !!childAccount.value,
            maxChars: inputsMaxChars.lastName,
        },
        email: {
            value: childAccount.email || '',
            label: 'email',
            placeholder: 'type.email',
            isDirty: !!childAccount.value,
            isValid: !!childAccount.email && isEmail(childAccount.email),
            maxChars: inputsMaxChars.email,
        },
    });

    const [roles, setRoles] = useState({});
    const [roleError, setRoleError] = useState(false);

    useEffect(() => {
        if (!Object.keys(roles).length) {
            const alreadyRoles = (childAccount.roles && childAccount.roles.toLowerCase().replace(/ /g, '.').split(',')) || [];
            const rolesObject = Object.values(authorities).reduce(
                (acc, cur) => ({ ...acc, [cur.authorityRole]: alreadyRoles.includes(cur.key) }),
                {}
            );
            setRoles(rolesObject);
        }
    }, [authorities, dispatch, childAccount, roles]);

    const handleValueChange = (event) => {
        const value = event.target.value;
        const keyName = event.target.name;
        const field = { ...account[keyName], value, isDirty: true };
        if (!field.maxChars || (field.maxChars && value.length <= field.maxChars)) {
            let errorMessage = null;
            if (keyName === 'email' && !isEmail(value)) errorMessage = 'invalid.email.format';
            if (field.isDirty && !value) errorMessage = 'required.field';
            field.errorMessage = errorMessage;
            setAccount({ ...account, [keyName]: field });
        }
    };

    const validateRoles = (roles) => {
        const hasARole = Object.keys(roles).reduce((doesHave, role) => doesHave || roles[role], false);
        if (!hasARole) return 'child.account.access.not.specified';
        const isIPRoleRequired = !!roles.ROLE_APPLICATION_SUBMIT;
        const areRolesInvalid = isIPRoleRequired && !roles.ROLE_TRADEMARKS && !roles.ROLE_DESIGNS;
        if (areRolesInvalid) return 'enable.ip.right.access';
        return '';
    };

    const handleRolesChange = (event) => {
        const newRoles = { ...roles, [event.target.name]: event.target.checked };
        const rError = validateRoles(newRoles);

        setRoleError(rError);
        setRoles(newRoles);
    };

    const saveChildAccount = (event) => {
        event.preventDefault();
        const { firstName, lastName, email } = account;

        const hasErrors = !!firstName.errorMessage || !!lastName.errorMessage || !!email.errorMessage;
        const hasValues = !!firstName.value && lastName.value && email.value;
        const rError = validateRoles(roles);
        const canProceed = hasValues && !hasErrors && !rError;

        if (!canProceed) {
            setRoleError(rError);
            setAccount({
                firstName: { ...account.firstName, isDirty: true, errorMessage: !account.firstName.value && 'required.field' },
                lastName: { ...account.lastName, isDirty: true, errorMessage: !account.lastName.value && 'required.field' },
                email: { ...account.email, isDirty: true, errorMessage: !account.email.value && 'required.field' },
            });
        } else {
            const updatedDetails = Object.keys(account).reduce(
                (acc, cur) => (account[cur].isDirty ? { ...acc, [cur]: account[cur].value } : { ...acc }),
                {}
            );

            const payload = {
                ...updatedDetails,
                userName: account.email.value,
                roles: Object.keys(roles).reduce((rolesArray, roleName) => {
                    if (roles[roleName]) rolesArray.push(roleName);
                    return rolesArray;
                }, []),
            };

            if (isEdit) delete payload.email;
            else delete payload.userName;

            saveAccount(payload);
        }
    };

    const inputItems = (
        <React.Fragment>
            {Object.keys(account).map((keyName, idx) => {
                const { value, label, placeholder, errorMessage } = account[keyName];
                const valueClass = !value ? classes.fieldValue : classes.linkValue;
                return (
                    <div key={`${keyName}_${idx}`}>
                        {readonly || (isEdit && keyName === 'email') ? (
                            <React.Fragment>
                                <Typography className={classes.fieldLabel}>{t(label)}</Typography>
                                <Typography className={valueClass}>{value || '-'}</Typography>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <InputLabel role="input" error={!!errorMessage} htmlFor={keyName} className={classes.fieldLabel}>
                                    {t(label)}*
                                </InputLabel>
                                <Input
                                    id={`${keyName}_${idx}`}
                                    role="input"
                                    placeholder={t(placeholder)}
                                    name={keyName}
                                    value={value}
                                    onChange={handleValueChange}
                                    className={classes.textInput}
                                    type="text"
                                    error={!!errorMessage}
                                    fullWidth
                                    autoFocus={idx === 0}
                                />
                                {!!errorMessage && <ErrorInput errorMessage={t(errorMessage)} classes={`${classes.inputError}`} />}
                            </React.Fragment>
                        )}
                    </div>
                );
            })}
        </React.Fragment>
    );

    return (
        <Card title={'account.details'} canEdit={true} readonly={false} saveCard={saveChildAccount} cancelCard={cancelAction}>
            {inputItems}
            <Typography className={classes.rolesTitle}>{t('role.details')}</Typography>
            <FormControl component="fieldset" className={classes.rolesForm}>
                <FormLabel className={classes.rolesLabel} component="legend">
                    {t('select.least.one.role')}*
                </FormLabel>
                {Object.values(authorities).map(({ key, authorityRole }, index) => (
                    <FormGroup key={`authority_${index}`}>
                        <Tooltip arrow placement={'right'} title={t(`${authorityRole.toLowerCase().replaceAll('_', '.')}.description`)}>
                            <FormControlLabel
                                className={classes.roleOption}
                                control={
                                    <Checkbox
                                        role="checkbox"
                                        checked={!!Object.keys(roles).length && !!roles[authorityRole]}
                                        onChange={handleRolesChange}
                                        name={authorityRole}
                                        checkedIcon={<CheckedIcon aria-hidden="true" focusable="false" />}
                                        icon={<UncheckedIcon aria-hidden="true" focusable="false" />}
                                    />
                                }
                                label={t(key)}
                            />
                        </Tooltip>
                    </FormGroup>
                ))}
            </FormControl>
            {!!roleError && <ErrorInput errorMessage={t(roleError)} classes={`${classes.inputError}`} />}
            {!!error && <ErrorInput errorMessage={t(error)} classes={`${classes.inputError}`} />}
        </Card>
    );
};

export default ChildAccountCard;
